import { isEventKey } from "keycode";
import debounce from "lodash/debounce";

import { isBreakpoint } from "../utils";

const SELECTORS = {
  wrapper: ".js-flyout",
  menuLink: ".js-flyout__button",
  menuPanel: ".js-flyout__content",
};

const CLASSES = {
  active: "nav-flyout--active",
  menuFirst: "nav-flyout__button",
  menuLast: "js-flyout__content-link--last",
};

class Flyout {
  constructor(context) {
    this.context = context;
    this.menuTrigger = null;
    this.menuPanel = null;
    this.menuPaddingLeft = 25;
    this.isIddle = false;
    this.dropdownOpen = false;
  }

  init() {
    this.menuTrigger = this.context.querySelector(SELECTORS.menuLink);
    this.menuPanel = this.context.querySelector(SELECTORS.menuPanel);

    this.subscribe();
  }

  subscribe() {
    this.menuTrigger.addEventListener("click", () => {
      this.toggleFlyout();
    });

    this.context.addEventListener("keydown", this.closeIfKey.bind(this));
    document.addEventListener("click", (e) => this.handleClickOutside(e));

    window.addEventListener(
      "scroll",
      debounce((e) => {
        this.handleScroll(e);
      }),
      300,
      {
        leading: true,
        trailing: false,
      },
    );

    window.addEventListener("resize", () => this.closeFlyout());
  }

  handleScroll(event) {
    if (!this.isIddle) {
      const currentScrollPosition =
        window.pageYOffset || document.documentElement.scrollTop;

      // Because of momentum scrolling on mobiles, we shouldn't continue if it is less than zero
      if (currentScrollPosition < 0) {
        return;
      }

      if (this.dropdownOpen) {
        if (isBreakpoint("large")) {
          this.closeFlyout();
        }
      }
    }
  }

  handleClickOutside({ target }) {
    const isClickInside = this.context.contains(target);

    if (!isClickInside && this.dropdownOpen) {
      this.closeFlyout();
    }
  }

  toggleFlyout() {
    if (isBreakpoint("large")) {
      const elementLeft = this.context.offsetLeft - this.menuPaddingLeft;
      this.context.style.left = `${elementLeft}px`;
    }

    if (this.menuPanel) {
      if (this.dropdownOpen) {
        this.closeFlyout();
      } else {
        this.openFlyout();
      }
    }

    return this;
  }

  closeIfKey(event) {
    const isFirstLink = event.target.classList.contains(CLASSES.menuFirst);
    const isLastLink = event.target.classList.contains(CLASSES.menuLast);
    const isTabPressed = isEventKey(event, "tab");
    const isEscPressed = isEventKey(event, "esc");
    const isShiftPressed = event.shiftKey;

    if (
      (!isShiftPressed && isLastLink && isTabPressed) ||
      (isFirstLink && isTabPressed && isShiftPressed) ||
      isEscPressed
    ) {
      return this.closeFlyout();
    }
  }

  openFlyout() {
    this.isIddle = true;
    this.menuPanel.removeAttribute("hidden");
    this.context.classList.add(CLASSES.active);
    this.context.setAttribute("aria-expanded", "true");

    if (!isBreakpoint("large")) {
      this.context.scrollIntoView();
    }

    this.dropdownOpen = true;
    setTimeout(() => {
      this.isIddle = false;
    }, 80);

    return this;
  }

  closeFlyout() {
    this.menuPanel.setAttribute("hidden", "");
    this.context.classList.remove(CLASSES.active);
    this.context.setAttribute("aria-expanded", "false");
    this.context.style.left = 0;

    this.menuTrigger.focus();

    this.dropdownOpen = false;
    this.isIddle = false;

    return this;
  }
}

export { Flyout };
